"use client"

import * as React from "react"
import {
  Terminal,
  KeyRound,
  Shield,
  Bot,
  Cpu,
  Layers,
  Zap,
  Code2,
  Lock,
  ExternalLink,
  Copy,
  Check,
  CheckCircle2,
  RefreshCw,
  Plus,
  Trash2,
  Globe,
  Sparkles,
  Info,
  ShieldCheck,
  Download,
  X,
  FileText,
  Code,
  Play,
  Clock,
} from "lucide-react"
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs"
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Account } from "@/types/workspace"
import { toast } from "@/lib/toast"

export interface DeveloperSettingsTabProps {
  devSubTab?: string
  setDevSubTab?: (subTab: string) => void
  apiKey: string
  handleGenerateApiKey: () => void
  webhookUrl: string
  setWebhookUrl: (url: string) => void
  handleSaveWebhook: () => void
  companySamlEnabled: boolean
  setCompanySamlEnabled: (val: boolean) => void
  companySamlIdpEntityId: string
  setCompanySamlIdpEntityId: (val: string) => void
  companySamlSsoUrl: string
  setCompanySamlSsoUrl: (val: string) => void
  companySamlCertificate: string
  setCompanySamlCertificate: (val: string) => void
  currentAccount: Account | null
  activeAccountId: string
  t: Record<string, string>
}

export function DeveloperSettingsTab({
  devSubTab = "api-keys",
  setDevSubTab,
  apiKey,
  handleGenerateApiKey,
  webhookUrl,
  setWebhookUrl,
  handleSaveWebhook,
  companySamlEnabled,
  setCompanySamlEnabled,
  companySamlIdpEntityId,
  setCompanySamlIdpEntityId,
  companySamlSsoUrl,
  setCompanySamlSsoUrl,
  companySamlCertificate,
  setCompanySamlCertificate,
  currentAccount,
  activeAccountId,
  t,
}: DeveloperSettingsTabProps) {
  // Local states for Developer tab (API Keys & SAML SSO)
  const [showNewKeyModal, setShowNewKeyModal] = React.useState(false)
  const [newKeyName, setNewKeyName] = React.useState("")
  const [newKeyExpiresDays, setNewKeyExpiresDays] = React.useState<number | "">("")
  const [newKeyScopes, setNewKeyScopes] = React.useState<string[]>(["read:contacts"])
  const [createdKey, setCreatedKey] = React.useState<any>(null)
  const [copiedKey, setCopiedKey] = React.useState(false)
  const [copiedEntryPoint, setCopiedEntryPoint] = React.useState(false)
  const [copiedEntityId, setCopiedEntityId] = React.useState(false)

  // Local states for SSO Hub & SAML metadata
  const [ssoHubTab, setSsoHubTab] = React.useState<"saml" | "sdk" | "oauth" | "guide">("saml")
  const [copiedMetadataUrl, setCopiedMetadataUrl] = React.useState(false)
  const [copiedCertPem, setCopiedCertPem] = React.useState(false)
  const [copiedTokenEndpoint, setCopiedTokenEndpoint] = React.useState(false)
  const [copiedVerifyEndpoint, setCopiedVerifyEndpoint] = React.useState(false)
  const [copiedOauthEndpoint, setCopiedOauthEndpoint] = React.useState(false)
  const [copiedCurlExample, setCopiedCurlExample] = React.useState(false)
  const [showCertDetails, setShowCertDetails] = React.useState(false)
  const [samlMetadataXml, setSamlMetadataXml] = React.useState<string | null>(null)
  const [samlCertPem, setSamlCertPem] = React.useState<string | null>(null)

  const originUrl = typeof window !== "undefined" ? window.location.origin : "https://alpha.boldworkspace.com.br"

  React.useEffect(() => {
    fetch("/api/saml/metadata")
      .then((res) => res.text())
      .then((xml) => {
        setSamlMetadataXml(xml)
        const match = xml.match(/<ds:X509Certificate>([\s\S]*?)<\/ds:X509Certificate>/)
        if (match && match[1]) {
          const cleanCert = match[1].trim()
          const formattedPem = "-----BEGIN CERTIFICATE-----\n" + (cleanCert.match(/.{1,64}/g)?.join("\n") || cleanCert) + "\n-----END CERTIFICATE-----"
          setSamlCertPem(formattedPem)
        }
      })
      .catch(() => { })
  }, [])

  const [apiKeyError, setApiKeyError] = React.useState<string | null>(null)
  const [isCreatingKey, setIsCreatingKey] = React.useState(false)

  // MCP (Model Context Protocol) states
  const [mcpEnabled, setMcpEnabled] = React.useState(true)
  const [mcpToken, setMcpToken] = React.useState("bold_mcp_live_9f8a3b2c1d4e7605")
  const [mcpConfigType, setMcpConfigType] = React.useState<"claude" | "cursor" | "windsurf" | "sdk">("claude")
  const [copiedMcpConfig, setCopiedMcpConfig] = React.useState(false)
  const [copiedMcpToken, setCopiedMcpToken] = React.useState(false)
  const [copiedMcpEndpoint, setCopiedMcpEndpoint] = React.useState(false)
  const [showMcpToken, setShowMcpToken] = React.useState(false)
  const [mcpTestingTool, setMcpTestingTool] = React.useState<string | null>(null)
  const [mcpTestResult, setMcpTestResult] = React.useState<string | null>(null)

  return (
            <TabsContent value="developer" className="outline-none space-y-6">
              <div>
                <h3 className="text-lg font-bold text-zinc-900 dark:text-white flex items-center gap-2 font-sans">
                  <Terminal className="size-5 text-[#004EB1]" />
                  Integrações & Conexões
                </h3>
                <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1 font-sans">
                  Gerencie chaves de acesso, notificações de eventos e login único corporativo para sua empresa.
                </p>
              </div>

              <Tabs value={devSubTab} onValueChange={(val) => setDevSubTab?.(val)} orientation="horizontal" className="w-full">
                <TabsList className="!flex !flex-row border-0 !bg-zinc-100 dark:!bg-zinc-900/50 p-1 gap-1 mb-4 !rounded-full !h-10 w-fit justify-start items-center">
                  <TabsTrigger
                    value="api-keys"
                    className="text-xs font-semibold text-zinc-500 dark:text-zinc-400 rounded-full bg-transparent hover:text-zinc-900 dark:hover:text-zinc-200 transition-all px-4 py-1.5 !h-8 cursor-pointer font-sans !w-auto !justify-center data-[state=active]:!bg-white dark:data-[state=active]:!bg-zinc-800 data-[state=active]:!text-zinc-950 dark:data-[state=active]:!text-white data-[state=active]:!shadow-md"
                  >
                    Chaves de Acesso
                  </TabsTrigger>
                  <TabsTrigger
                    value="webhooks"
                    className="text-xs font-semibold text-zinc-500 dark:text-zinc-400 rounded-full bg-transparent hover:text-zinc-900 dark:hover:text-zinc-200 transition-all px-4 py-1.5 !h-8 cursor-pointer font-sans !w-auto !justify-center data-[state=active]:!bg-white dark:data-[state=active]:!bg-zinc-800 data-[state=active]:!text-zinc-950 dark:data-[state=active]:!text-white data-[state=active]:!shadow-md"
                  >
                    Notificações de Eventos
                  </TabsTrigger>
                  <TabsTrigger
                    value="saml-sso"
                    className="text-xs font-semibold text-zinc-500 dark:text-zinc-400 rounded-full bg-transparent hover:text-zinc-900 dark:hover:text-zinc-200 transition-all px-4 py-1.5 !h-8 cursor-pointer font-sans !w-auto !justify-center data-[state=active]:!bg-white dark:data-[state=active]:!bg-zinc-800 data-[state=active]:!text-zinc-950 dark:data-[state=active]:!text-white data-[state=active]:!shadow-md"
                  >
                    Login Único Corporativo (SSO)
                  </TabsTrigger>
                  <TabsTrigger
                    value="mcp"
                    className="text-xs font-semibold text-zinc-500 dark:text-zinc-400 rounded-full bg-transparent hover:text-zinc-900 dark:hover:text-zinc-200 transition-all px-4 py-1.5 !h-8 cursor-pointer font-sans !w-auto !justify-center data-[state=active]:!bg-[#004EB1] dark:data-[state=active]:!bg-[#004EB1] data-[state=active]:!text-white dark:data-[state=active]:!text-white data-[state=active]:!shadow-md flex items-center gap-1.5"
                  >
                    <Bot className="size-3.5" />
                    Servidor MCP
                  </TabsTrigger>
                  <TabsTrigger
                    value="toasts"
                    className="text-xs font-semibold text-zinc-500 dark:text-zinc-400 rounded-full bg-transparent hover:text-zinc-900 dark:hover:text-zinc-200 transition-all px-4 py-1.5 !h-8 cursor-pointer font-sans !w-auto !justify-center data-[state=active]:!bg-white dark:data-[state=active]:!bg-zinc-800 data-[state=active]:!text-zinc-950 dark:data-[state=active]:!text-white data-[state=active]:!shadow-md flex items-center gap-1.5"
                  >
                    <Sparkles className="size-3.5 text-[#004EB1]" />
                    Notificações Toast
                  </TabsTrigger>
                </TabsList>

                {/* SUB-ABA 1: CHAVES DE API */}
                <TabsContent value="api-keys" className="outline-none space-y-4">
                  <Card className="border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 font-sans">
                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                      <div>
                        <CardTitle className="text-sm font-bold flex items-center gap-2 text-[#004EB1]">
                          <KeyRound className="size-4" />
                          Chaves de API Secretas (Secret Keys)
                        </CardTitle>
                        <CardDescription className="text-xs mt-1 font-sans">
                          Gerencie chaves para integração e acesso programático REST ao ecossistema Bold.
                        </CardDescription>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => {
                          setShowNewKeyModal(true)
                          setCreatedKey(null)
                          setApiKeyError(null)
                        }}
                        className="bg-[#004EB1] hover:bg-[#003d8d] text-white text-xs h-8 px-3 font-sans cursor-pointer"
                      >
                        Nova Chave
                      </Button>
                    </CardHeader>
                    <CardContent className="space-y-4 font-sans">
                      {/* Banner de Sucesso */}
                      {createdKey && (
                        <div className="rounded-lg bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-900/30 p-4 space-y-3">
                          <h4 className="text-xs font-bold text-emerald-800 dark:text-emerald-400 flex items-center gap-1.5 font-sans">
                            <CheckCircle2 className="size-4" />
                            Chave de API Criada com Sucesso!
                          </h4>
                          <p className="text-[11px] text-zinc-600 dark:text-zinc-400 font-sans">
                            Copie esta chave agora. Por razões de segurança, ela não poderá ser exibida novamente.
                          </p>
                          <div className="flex gap-2">
                            <code className="flex-1 rounded bg-zinc-100 dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800 px-3 py-1.5 font-mono text-xs font-bold text-zinc-800 dark:text-zinc-200 select-all truncate">
                              {createdKey.secretKey}
                            </code>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                navigator.clipboard.writeText(createdKey.secretKey)
                                setCopiedKey(true)
                                setTimeout(() => setCopiedKey(false), 2000)
                              }}
                              className="h-8 text-xs shrink-0 font-sans cursor-pointer"
                            >
                              {copiedKey ? "Copiado!" : "Copiar"}
                            </Button>
                          </div>
                          <div className="flex justify-end">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setCreatedKey(null)}
                              className="text-xs text-zinc-500 hover:text-zinc-700 h-7 font-sans cursor-pointer"
                            >
                              Fechar aviso
                            </Button>
                          </div>
                        </div>
                      )}

                      {/* Formulário de Criação de Nova Chave */}
                      {showNewKeyModal && !createdKey && (
                        <form
                          onSubmit={(e) => {
                            e.preventDefault()
                            if (!newKeyName) return
                            setIsCreatingKey(true)
                            setTimeout(() => {
                              const generatedKey = `bold_sk_live_${Math.random().toString(36).substring(2, 15)}${Math.random().toString(36).substring(2, 15)}`
                              const newObj = {
                                id: `key-${Date.now()}`,
                                name: newKeyName,
                                secretKey: generatedKey,
                                prefix: generatedKey.substring(0, 14),
                                scopes: newKeyScopes,
                                createdAt: Date.now().toString(),
                                expiresAt: newKeyExpiresDays ? (Date.now() + Number(newKeyExpiresDays) * 86400000).toString() : null,
                              }
                              setCreatedKey(newObj)
                              setIsCreatingKey(false)
                              setShowNewKeyModal(false)
                              setNewKeyName("")
                            }, 500)
                          }}
                          className="rounded-lg border border-zinc-200 dark:border-zinc-800 p-4 bg-zinc-50 dark:bg-zinc-950/50 space-y-4"
                        >
                          <h4 className="text-xs font-bold text-zinc-800 dark:text-zinc-200 font-sans">
                            Gerar Nova Chave de API
                          </h4>

                          <div className="space-y-1">
                            <label className="text-[11px] font-bold text-zinc-500 font-sans">Nome da Chave</label>
                            <input
                              type="text"
                              placeholder="Ex: Integração CRM Externa"
                              value={newKeyName}
                              onChange={(e) => setNewKeyName(e.target.value)}
                              className="w-full rounded-md border border-zinc-200 px-3 py-1.5 text-xs focus-visible:border-[#004EB1] focus-visible:outline-none dark:border-zinc-800 dark:bg-zinc-950 dark:text-white font-sans"
                              required
                            />
                          </div>

                          <div className="space-y-1.5">
                            <label className="text-[11px] font-bold text-zinc-500 font-sans">Expiração (em dias, deixe em branco para nunca expirar)</label>
                            <input
                              type="number"
                              placeholder="Nunca expira"
                              value={newKeyExpiresDays}
                              onChange={(e) => setNewKeyExpiresDays(e.target.value === "" ? "" : parseInt(e.target.value))}
                              className="w-full rounded-md border border-zinc-200 px-3 py-1.5 text-xs focus-visible:border-[#004EB1] focus-visible:outline-none dark:border-zinc-800 dark:bg-zinc-950 dark:text-white font-sans"
                              min={1}
                            />
                          </div>

                          <div className="space-y-1.5">
                            <label className="text-[11px] font-bold text-zinc-500 font-sans">Escopos autorizados</label>
                            <div className="flex flex-wrap gap-2 pt-0.5">
                              {["read:contacts", "write:contacts", "admin"].map((scope) => (
                                <button
                                  type="button"
                                  key={scope}
                                  onClick={() => {
                                    if (newKeyScopes.includes(scope)) {
                                      setNewKeyScopes(newKeyScopes.filter(s => s !== scope))
                                    } else {
                                      setNewKeyScopes([...newKeyScopes, scope])
                                    }
                                  }}
                                  className={`rounded px-2.5 py-1 text-[10px] font-semibold border transition-all cursor-pointer ${newKeyScopes.includes(scope)
                                    ? "bg-blue-50 text-[#004EB1] border-[#004EB1] dark:bg-blue-950/40 dark:text-blue-400 dark:border-blue-800"
                                    : "bg-white text-zinc-500 border-zinc-200 hover:border-zinc-300 dark:bg-zinc-900 dark:border-zinc-800"
                                    }`}
                                >
                                  {scope}
                                </button>
                              ))}
                            </div>
                          </div>

                          <div className="flex justify-end gap-2 pt-2">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => setShowNewKeyModal(false)}
                              className="text-xs h-8 font-sans cursor-pointer"
                            >
                              Cancelar
                            </Button>
                            <Button
                              type="submit"
                              disabled={isCreatingKey}
                              className="bg-[#004EB1] hover:bg-[#003d8d] text-white text-xs h-8 px-3 font-sans cursor-pointer"
                            >
                              {isCreatingKey ? "Gerando..." : "Gerar Chave"}
                            </Button>
                          </div>
                        </form>
                      )}

                      {/* Chave Padrão Ativa */}
                      <div className="space-y-2">
                        <label className="text-[11px] font-bold text-zinc-500 dark:text-zinc-400 font-sans">Chave Principal do Workspace</label>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            readOnly
                            value={apiKey || "bold_sk_live_master_workspace_key_default"}
                            className="flex-1 rounded-md border border-zinc-200 px-3 py-2 text-xs bg-zinc-50 dark:bg-zinc-950 dark:border-zinc-800 text-zinc-700 dark:text-zinc-300 font-mono"
                          />
                          <Button
                            type="button"
                            onClick={handleGenerateApiKey}
                            className="bg-[#004EB1] hover:bg-[#003d8d] text-white text-xs font-semibold h-9 px-4 cursor-pointer"
                          >
                            {apiKey ? "Regerar" : "Gerar Chave"}
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* SUB-ABA 2: WEBHOOKS */}
                <TabsContent value="webhooks" className="outline-none space-y-4 font-sans">
                  <Card className="border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900">
                    <CardHeader>
                      <CardTitle className="text-sm font-bold text-zinc-800 dark:text-zinc-100 font-sans flex items-center gap-2">
                        <Globe className="size-4 text-[#004EB1]" />
                        Configuração de Webhooks de Eventos
                      </CardTitle>
                      <CardDescription className="text-xs font-sans">
                        Receba notificações HTTP POST em tempo real sobre eventos de usuários, logins e alterações no workspace.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="space-y-1.5">
                        <label className="text-[11px] font-bold text-zinc-500 dark:text-zinc-400 font-sans">Endpoint URL do Webhook</label>
                        <div className="flex gap-2">
                          <input
                            type="url"
                            placeholder="https://api.empresa.com.br/webhooks/bold"
                            value={webhookUrl}
                            onChange={(e) => setWebhookUrl(e.target.value)}
                            className="flex-1 rounded-md border border-zinc-200 px-3 py-2 text-xs bg-zinc-50 dark:bg-zinc-950 dark:border-zinc-800 text-zinc-700 dark:text-zinc-300 font-mono focus-visible:outline-none focus-visible:border-[#004EB1]"
                          />
                          <Button
                            type="button"
                            onClick={handleSaveWebhook}
                            className="bg-[#004EB1] hover:bg-[#003d8d] text-white text-xs font-semibold h-9 px-4 cursor-pointer"
                          >
                            Salvar Endpoint
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* SUB-ABA 3: SAML SSO & DEVELOPER AUTH HUB */}
                <TabsContent value="saml-sso" className="outline-none space-y-5 font-sans">
                  {/* Status Banner */}
                  <Card className="border-blue-200 dark:border-blue-900/40 bg-gradient-to-r from-blue-50/80 via-white to-blue-50/30 dark:from-blue-950/20 dark:via-zinc-900 dark:to-zinc-900 shadow-sm font-sans">
                    <CardContent className="p-5 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                      <div className="flex items-center gap-3.5">
                        <div className="size-11 rounded-2xl bg-[#004EB1] text-white flex items-center justify-center shadow-md shrink-0">
                          <ShieldCheck className="size-6 text-white" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="text-sm font-extrabold text-zinc-900 dark:text-white">Central de Autenticação SSO & Integrações</h4>
                            <span className="inline-flex items-center gap-1 text-[10px] font-extrabold bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 px-2 py-0.5 rounded-full border border-emerald-500/30">
                              <span className="size-1.5 rounded-full bg-emerald-500 animate-pulse" />
                              IdP Operacional
                            </span>
                          </div>
                          <p className="text-xs text-zinc-600 dark:text-zinc-400 mt-1">
                            Credenciais, metadados SAML 2.0, tokens iFrame SSO e autenticação OAuth 2.0 para desenvolvedores de aplicativos.
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Seletor de Sub-Abas SSO */}
                  <div className="flex border-b border-zinc-200 dark:border-zinc-800 gap-2 pb-0">
                    <button
                      type="button"
                      onClick={() => setSsoHubTab("saml")}
                      className={`px-4 py-2 text-xs font-bold transition-all border-b-2 cursor-pointer ${ssoHubTab === "saml"
                        ? "border-[#004EB1] text-[#004EB1] dark:text-blue-400"
                        : "border-transparent text-zinc-500 hover:text-zinc-800 dark:text-zinc-400 dark:hover:text-white"
                        }`}
                    >
                      SAML 2.0 (IdP)
                    </button>
                    <button
                      type="button"
                      onClick={() => setSsoHubTab("sdk")}
                      className={`px-4 py-2 text-xs font-bold transition-all border-b-2 cursor-pointer ${ssoHubTab === "sdk"
                        ? "border-[#004EB1] text-[#004EB1] dark:text-blue-400"
                        : "border-transparent text-zinc-500 hover:text-zinc-800 dark:text-zinc-400 dark:hover:text-white"
                        }`}
                    >
                      Bold SDK & iFrame Token
                    </button>
                    <button
                      type="button"
                      onClick={() => setSsoHubTab("oauth")}
                      className={`px-4 py-2 text-xs font-bold transition-all border-b-2 cursor-pointer ${ssoHubTab === "oauth"
                        ? "border-[#004EB1] text-[#004EB1] dark:text-blue-400"
                        : "border-transparent text-zinc-500 hover:text-zinc-800 dark:text-zinc-400 dark:hover:text-white"
                        }`}
                    >
                      OAuth 2.0 Client Credentials
                    </button>
                    <button
                      type="button"
                      onClick={() => setSsoHubTab("guide")}
                      className={`px-4 py-2 text-xs font-bold transition-all border-b-2 cursor-pointer ${ssoHubTab === "guide"
                        ? "border-[#004EB1] text-[#004EB1] dark:text-blue-400"
                        : "border-transparent text-zinc-500 hover:text-zinc-800 dark:text-zinc-400 dark:hover:text-white"
                        }`}
                    >
                      Exemplos de Código
                    </button>
                  </div>

                  {/* CONTEÚDO DA ABA 1: SAML 2.0 */}
                  {ssoHubTab === "saml" && (
                    <Card className="border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-sm font-bold text-zinc-900 dark:text-white flex items-center gap-2 font-sans">
                          <KeyRound className="size-4 text-[#004EB1]" />
                          Parâmetros de Configuração SAML 2.0 (Identity Provider)
                        </CardTitle>
                        <CardDescription className="text-xs font-sans">
                          Copie as URLs e o certificado abaixo para configurar seu aplicativo externo (Service Provider) para aceitar login via Bold Workspace.
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-5 font-sans">
                        {/* SAML SSO URL / ACS */}
                        <div className="space-y-1.5">
                          <label className="text-[11px] font-bold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider font-sans">
                            SAML Single Sign-On URL (ACS / Entry Point)
                          </label>
                          <div className="flex gap-2">
                            <input
                              type="text"
                              readOnly
                              value={`${originUrl}/api/saml/sso`}
                              className="flex-1 rounded bg-zinc-50 dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800 px-3 py-1.5 font-mono text-xs text-zinc-800 dark:text-zinc-200 select-all focus:outline-none"
                            />
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                navigator.clipboard.writeText(`${originUrl}/api/saml/sso`)
                                setCopiedEntryPoint(true)
                                setTimeout(() => setCopiedEntryPoint(false), 2000)
                              }}
                              className="h-8 text-xs shrink-0 font-sans cursor-pointer"
                            >
                              {copiedEntryPoint ? "Copiado!" : "Copiar URL"}
                            </Button>
                          </div>
                        </div>

                        {/* SAML Issuer / Entity ID */}
                        <div className="space-y-1.5">
                          <label className="text-[11px] font-bold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider font-sans">
                            SAML Issuer (Entity ID / Audience)
                          </label>
                          <div className="flex gap-2">
                            <input
                              type="text"
                              readOnly
                              value={`${originUrl}/api/saml/metadata`}
                              className="flex-1 rounded bg-zinc-50 dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800 px-3 py-1.5 font-mono text-xs text-zinc-800 dark:text-zinc-200 select-all focus:outline-none"
                            />
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                navigator.clipboard.writeText(`${originUrl}/api/saml/metadata`)
                                setCopiedEntityId(true)
                                setTimeout(() => setCopiedEntityId(false), 2000)
                              }}
                              className="h-8 text-xs shrink-0 font-sans cursor-pointer"
                            >
                              {copiedEntityId ? "Copiado!" : "Copiar ID"}
                            </Button>
                          </div>
                        </div>

                        {/* Metadata XML URL */}
                        <div className="space-y-1.5">
                          <label className="text-[11px] font-bold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider font-sans">
                            SAML Metadata XML Endpoint
                          </label>
                          <div className="flex gap-2">
                            <input
                              type="text"
                              readOnly
                              value={`${originUrl}/api/saml/metadata`}
                              className="flex-1 rounded bg-zinc-50 dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800 px-3 py-1.5 font-mono text-xs text-zinc-800 dark:text-zinc-200 select-all focus:outline-none"
                            />
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                navigator.clipboard.writeText(`${originUrl}/api/saml/metadata`)
                                setCopiedMetadataUrl(true)
                                setTimeout(() => setCopiedMetadataUrl(false), 2000)
                              }}
                              className="h-8 text-xs shrink-0 font-sans cursor-pointer"
                            >
                              {copiedMetadataUrl ? "Copiado!" : "Copiar URL"}
                            </Button>
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => {
                                if (samlMetadataXml) {
                                  const blob = new Blob([samlMetadataXml], { type: "application/xml" })
                                  const url = URL.createObjectURL(blob)
                                  const a = document.createElement("a")
                                  a.href = url
                                  a.download = "metadata.xml"
                                  a.click()
                                }
                              }}
                              className="h-8 text-xs shrink-0 font-sans cursor-pointer"
                            >
                              <Download className="size-3.5 mr-1" />
                              Baixar XML
                            </Button>
                          </div>
                        </div>

                        {/* Certificado X.509 */}
                        <div className="space-y-2 pt-2 border-t border-zinc-100 dark:border-zinc-800">
                          <div className="flex items-center justify-between">
                            <label className="text-[11px] font-bold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider font-sans">
                              Certificado X.509 do IdP (Assinatura RSA)
                            </label>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  if (samlCertPem) {
                                    navigator.clipboard.writeText(samlCertPem)
                                    setCopiedCertPem(true)
                                    setTimeout(() => setCopiedCertPem(false), 2000)
                                  }
                                }}
                                className="h-7 text-[11px] font-sans cursor-pointer"
                              >
                                {copiedCertPem ? "Copiado!" : "Copiar Certificado PEM"}
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setShowCertDetails(!showCertDetails)}
                                className="h-7 text-[11px] font-sans cursor-pointer"
                              >
                                {showCertDetails ? "Ocultar PEM" : "Ver PEM"}
                              </Button>
                            </div>
                          </div>

                          {showCertDetails && samlCertPem && (
                            <textarea
                              readOnly
                              rows={6}
                              value={samlCertPem}
                              className="w-full rounded bg-zinc-900 text-zinc-200 p-3 font-mono text-[11px] select-all border border-zinc-800 focus:outline-none"
                            />
                          )}
                        </div>

                        {/* Tabela de Atributos SAML */}
                        <div className="space-y-2 pt-2 border-t border-zinc-100 dark:border-zinc-800 font-sans">
                          <h5 className="text-[11px] font-bold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider font-sans">
                            Atributos Padrão Transmitidos na Resposta SAML
                          </h5>
                          <div className="rounded-lg border border-zinc-200 dark:border-zinc-800 overflow-hidden text-xs font-sans">
                            <table className="w-full text-left">
                              <thead className="bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300 font-bold">
                                <tr>
                                  <th className="p-2.5">Nome do Atributo</th>
                                  <th className="p-2.5">Formato</th>
                                  <th className="p-2.5">Exemplo de Valor</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-zinc-150 dark:divide-zinc-800 text-zinc-700 dark:text-zinc-300 font-sans">
                                <tr>
                                  <td className="p-2.5 font-mono text-[#004EB1]">NameID</td>
                                  <td className="p-2.5 text-zinc-400">emailAddress</td>
                                  <td className="p-2.5 font-mono">usuario@empresa.com.br</td>
                                </tr>
                                <tr>
                                  <td className="p-2.5 font-mono text-[#004EB1]">email</td>
                                  <td className="p-2.5 text-zinc-400">basic</td>
                                  <td className="p-2.5 font-mono">usuario@empresa.com.br</td>
                                </tr>
                                <tr>
                                  <td className="p-2.5 font-mono text-[#004EB1]">name</td>
                                  <td className="p-2.5 text-zinc-400">basic</td>
                                  <td className="p-2.5 font-mono">João da Silva</td>
                                </tr>
                                <tr>
                                  <td className="p-2.5 font-mono text-[#004EB1]">userId</td>
                                  <td className="p-2.5 text-zinc-400">basic</td>
                                  <td className="p-2.5 font-mono">uuid-1234-abcd</td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* CONTEÚDO DA ABA 2: BOLD SDK & iFRAME TOKEN */}
                  {ssoHubTab === "sdk" && (
                    <Card className="border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 font-sans">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-sm font-bold text-zinc-900 dark:text-white flex items-center gap-2 font-sans">
                          <Code2 className="size-4 text-[#004EB1]" />
                          Bold SDK & Autenticação iFrame SSO
                        </CardTitle>
                        <CardDescription className="text-xs font-sans">
                          Para aplicativos integrados rodando dentro do iframe do Bold Workspace.
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4 font-sans">
                        <div className="space-y-1.5">
                          <label className="text-[11px] font-bold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider font-sans">
                            1. Endpoint de Emissão de SSO Token
                          </label>
                          <div className="flex gap-2">
                            <input
                              type="text"
                              readOnly
                              value={`POST ${originUrl}/api/v1/sso/token`}
                              className="flex-1 rounded bg-zinc-50 dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800 px-3 py-1.5 font-mono text-xs text-zinc-800 dark:text-zinc-200 select-all focus:outline-none"
                            />
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                navigator.clipboard.writeText(`${originUrl}/api/v1/sso/token`)
                                setCopiedTokenEndpoint(true)
                                setTimeout(() => setCopiedTokenEndpoint(false), 2000)
                              }}
                              className="h-8 text-xs shrink-0 font-sans cursor-pointer"
                            >
                              {copiedTokenEndpoint ? "Copiado!" : "Copiar Endpoint"}
                            </Button>
                          </div>
                        </div>

                        <div className="space-y-1.5">
                          <label className="text-[11px] font-bold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider font-sans">
                            2. Endpoint de Verificação pelo Backend do App
                          </label>
                          <div className="flex gap-2">
                            <input
                              type="text"
                              readOnly
                              value={`POST ${originUrl}/api/v1/sso/token/verify`}
                              className="flex-1 rounded bg-zinc-50 dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800 px-3 py-1.5 font-mono text-xs text-zinc-800 dark:text-zinc-200 select-all focus:outline-none"
                            />
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                navigator.clipboard.writeText(`${originUrl}/api/v1/sso/token/verify`)
                                setCopiedVerifyEndpoint(true)
                                setTimeout(() => setCopiedVerifyEndpoint(false), 2000)
                              }}
                              className="h-8 text-xs shrink-0 font-sans cursor-pointer"
                            >
                              {copiedVerifyEndpoint ? "Copiado!" : "Copiar Endpoint"}
                            </Button>
                          </div>
                        </div>

                        <div className="rounded-lg bg-zinc-900 p-4 text-zinc-100 space-y-2 font-mono text-xs">
                          <p className="text-[#004EB1] font-bold">{"// Exemplo de Body enviado para /verify pelo seu app:"}</p>
                          <pre className="text-emerald-400">
                            {`{
  "token": "sso_token_recebido_no_iframe",
  "clientId": "seu_client_id",
  "clientSecret": "seu_client_secret"
}`}
                          </pre>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* CONTEÚDO DA ABA 3: OAUTH 2.0 */}
                  {ssoHubTab === "oauth" && (
                    <Card className="border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 font-sans">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-sm font-bold text-zinc-900 dark:text-white flex items-center gap-2 font-sans">
                          <Zap className="size-4 text-[#004EB1]" />
                          OAuth 2.0 Client Credentials (API REST)
                        </CardTitle>
                        <CardDescription className="text-xs font-sans">
                          Troque suas credenciais (Client ID e Secret Key) por um Bearer JWT Token para fazer requisições REST nas APIs do Bold Workspace.
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4 font-sans">
                        <div className="space-y-1.5">
                          <label className="text-[11px] font-bold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider font-sans">
                            OAuth 2.0 Token Endpoint
                          </label>
                          <div className="flex gap-2">
                            <input
                              type="text"
                              readOnly
                              value={`POST ${originUrl}/api/v1/auth/token`}
                              className="flex-1 rounded bg-zinc-50 dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800 px-3 py-1.5 font-mono text-xs text-zinc-800 dark:text-zinc-200 select-all focus:outline-none"
                            />
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                navigator.clipboard.writeText(`${originUrl}/api/v1/auth/token`)
                                setCopiedOauthEndpoint(true)
                                setTimeout(() => setCopiedOauthEndpoint(false), 2000)
                              }}
                              className="h-8 text-xs shrink-0 font-sans cursor-pointer"
                            >
                              {copiedOauthEndpoint ? "Copiado!" : "Copiar Endpoint"}
                            </Button>
                          </div>
                        </div>

                        <div className="rounded-lg bg-zinc-900 p-4 text-zinc-100 space-y-2 font-mono text-xs">
                          <p className="text-amber-400 font-bold">{"// Exemplo de chamada cURL:"}</p>
                          <pre className="text-zinc-300 overflow-x-auto p-2 bg-zinc-950 rounded border border-zinc-800">
                            {`curl -X POST "${originUrl}/api/v1/auth/token" \\
  -H "Content-Type: application/json" \\
  -d '{
    "clientId": "seu_client_id",
    "clientSecret": "seu_client_secret",
    "grantType": "client_credentials"
  }'`}
                          </pre>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* CONTEÚDO DA ABA 4: GUIA DO DESENVOLVEDOR & EXEMPLOS */}
                  {ssoHubTab === "guide" && (
                    <Card className="border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 font-sans">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-sm font-bold text-zinc-900 dark:text-white flex items-center gap-2 font-sans">
                          <FileText className="size-4 text-[#004EB1]" />
                          Exemplos Práticos de Integração de SSO para Desenvolvedores
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4 font-sans">
                        <div className="space-y-2">
                          <h5 className="text-xs font-bold text-zinc-800 dark:text-zinc-200 font-sans">
                            1. Validando o SSO Token em Node.js / Express:
                          </h5>
                          <pre className="rounded bg-zinc-900 text-zinc-200 p-3 font-mono text-[11px] overflow-x-auto">
                            {`const express = require('express');
const axios = require('axios');
const app = express();

app.post('/api/sso-login', async (req, res) => {
  const { ssoToken } = req.body;
  try {
    const verifyRes = await axios.post('${originUrl}/api/v1/sso/token/verify', {
      token: ssoToken,
      clientId: process.env.BOLD_CLIENT_ID,
      clientSecret: process.env.BOLD_CLIENT_SECRET
    });
    
    // Retorna { valid: true, user: { id, email, name }, workspaceId }
    const { user, workspaceId } = verifyRes.data;
    req.session.userId = user.id;
    return res.json({ success: true, user });
  } catch (err) {
    return res.status(401).json({ error: 'Token SSO inválido' });
  }
});`}
                          </pre>
                        </div>

                        <div className="space-y-2 pt-2 border-t border-zinc-100 dark:border-zinc-800 font-sans">
                          <h5 className="text-xs font-bold text-zinc-800 dark:text-zinc-200 font-sans">
                            2. Pacote SDK Oficial do Bold Workspace:
                          </h5>
                          <p className="text-xs text-zinc-600 dark:text-zinc-400 font-sans">
                            Instale a SDK privada oficial no seu projeto Node.js:
                          </p>
                          <pre className="rounded bg-zinc-950 text-emerald-400 p-2.5 font-mono text-[11px]">
                            npm install @boldworkspace/sdk --registry=https://npm.boldworkspace.com.br/
                          </pre>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </TabsContent>

                {/* SUB-ABA 4: SERVIDOR MCP (MODEL CONTEXT PROTOCOL) */}
                <TabsContent value="mcp" className="outline-none space-y-5 font-sans">
                  {/* Status Banner */}
                  <Card className="border-blue-200 dark:border-blue-900/40 bg-gradient-to-r from-blue-50/80 via-white to-blue-50/30 dark:from-blue-950/20 dark:via-zinc-900 dark:to-zinc-900 shadow-sm font-sans">
                    <CardContent className="p-5 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                      <div className="flex items-center gap-3.5">
                        <div className="size-11 rounded-2xl bg-[#004EB1] text-white flex items-center justify-center shadow-md shrink-0">
                          <Bot className="size-6 text-white" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="text-sm font-extrabold text-zinc-900 dark:text-white">Servidor MCP Bold Workspace</h4>
                            <span className="inline-flex items-center gap-1 text-[10px] font-extrabold bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 px-2 py-0.5 rounded-full border border-emerald-500/30">
                              <span className="size-1.5 rounded-full bg-emerald-500 animate-pulse" />
                              {mcpEnabled ? "Ativo & Operacional" : "Pausado"}
                            </span>
                          </div>
                          <p className="text-xs text-zinc-600 dark:text-zinc-400 mt-1">
                            Conecte assistentes de IA (Cursor, Claude Desktop, Antigravity, Windsurf) diretamente ao seu ecossistema Bold.
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 self-end md:self-auto shrink-0">
                        <Button
                          size="sm"
                          variant={mcpEnabled ? "outline" : "default"}
                          onClick={() => {
                            setMcpEnabled(!mcpEnabled)
                            toast.success(mcpEnabled ? "Servidor MCP pausado." : "Servidor MCP ativado.")
                          }}
                          className="text-xs h-8 px-3 font-semibold cursor-pointer"
                        >
                          {mcpEnabled ? "Pausar Servidor" : "Ativar Servidor"}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Endpoints & Autenticação MCP */}
                  <Card className="border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-bold text-zinc-900 dark:text-white flex items-center gap-2">
                        <Cpu className="size-4 text-[#004EB1]" />
                        Endpoints & Token de Conexão MCP
                      </CardTitle>
                      <CardDescription className="text-xs">
                        Utilize estes endpoints e token Bearer para conectar clientes MCP via SSE ou HTTP POST.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* SSE Endpoint */}
                      <div className="space-y-1.5">
                        <label className="text-[11px] font-bold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">
                          SSE Endpoint (Server-Sent Events)
                        </label>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            readOnly
                            value="https://admin.boldworkspace.com.br/api/mcp/sse"
                            className="flex-1 rounded-md bg-zinc-50 dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800 px-3 py-1.5 font-mono text-xs text-zinc-800 dark:text-zinc-200 select-all"
                          />
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              navigator.clipboard.writeText("https://admin.boldworkspace.com.br/api/mcp/sse")
                              setCopiedMcpEndpoint(true)
                              setTimeout(() => setCopiedMcpEndpoint(false), 2000)
                            }}
                            className="h-8 text-xs shrink-0 cursor-pointer"
                          >
                            {copiedMcpEndpoint ? "Copiado!" : "Copiar Endpoint"}
                          </Button>
                        </div>
                      </div>

                      {/* Token de Acesso */}
                      <div className="space-y-1.5">
                        <div className="flex items-center justify-between">
                          <label className="text-[11px] font-bold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">
                            Token de Autenticação MCP (Bearer Secret)
                          </label>
                          <button
                            onClick={() => setShowMcpToken(!showMcpToken)}
                            className="text-[10px] font-bold text-blue-500 hover:underline cursor-pointer"
                          >
                            {showMcpToken ? "Ocultar Secret" : "Mostrar Secret"}
                          </button>
                        </div>
                        <div className="flex gap-2">
                          <input
                            type={showMcpToken ? "text" : "password"}
                            readOnly
                            value={mcpToken}
                            className="flex-1 rounded-md bg-zinc-50 dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800 px-3 py-1.5 font-mono text-xs text-zinc-800 dark:text-zinc-200 select-all"
                          />
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              navigator.clipboard.writeText(mcpToken)
                              setCopiedMcpToken(true)
                              setTimeout(() => setCopiedMcpToken(false), 2000)
                            }}
                            className="h-8 text-xs shrink-0 cursor-pointer"
                          >
                            {copiedMcpToken ? "Copiado!" : "Copiar Token"}
                          </Button>
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => {
                              const newToken = `bold_mcp_live_${Math.random().toString(36).substring(2, 10)}${Math.random().toString(36).substring(2, 10)}`
                              setMcpToken(newToken)
                              toast.success("Novo token MCP gerado com sucesso!")
                            }}
                            className="h-8 text-xs shrink-0 cursor-pointer"
                          >
                            <RefreshCw className="size-3 mr-1" /> Regerar
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Gerador de Configuração JSON (1-Click Setup) */}
                  <Card className="border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-bold text-zinc-900 dark:text-white flex items-center gap-2">
                        <Code2 className="size-4 text-[#004EB1]" />
                        Gerador de Configuração Rápida (JSON para IDEs & Agentes)
                      </CardTitle>
                      <CardDescription className="text-xs">
                        Copie a configuração pronta para ativar o Bold Workspace no seu assistente de IA favorito.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {/* Seleção do Cliente/IDE */}
                      <div className="flex gap-2 border-b border-zinc-200 dark:border-zinc-800 pb-3">
                        {[
                          { id: "claude", label: "Claude Desktop" },
                          { id: "cursor", label: "Cursor AI" },
                          { id: "windsurf", label: "Windsurf / Antigravity" },
                          { id: "sdk", label: "Node.js SDK" },
                        ].map((item) => (
                          <button
                            key={item.id}
                            onClick={() => setMcpConfigType(item.id as any)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${mcpConfigType === item.id
                              ? "bg-[#004EB1] text-white shadow-sm"
                              : "bg-zinc-100 dark:bg-zinc-800/60 text-zinc-600 dark:text-zinc-400 hover:bg-zinc-200 dark:hover:bg-zinc-800"
                              }`}
                          >
                            {item.label}
                          </button>
                        ))}
                      </div>

                      {/* Snippet do Código */}
                      <div className="relative">
                        <pre className="rounded-xl bg-zinc-950 p-4 font-mono text-[11px] text-emerald-400 overflow-x-auto border border-zinc-800 leading-relaxed">
                          {mcpConfigType === "claude" && JSON.stringify({
                            mcpServers: {
                              "bold-workspace": {
                                command: "npx",
                                args: ["-y", "@boldworkspace/mcp-server@latest"],
                                env: {
                                  BOLD_WORKSPACE_API_KEY: mcpToken,
                                  BOLD_WORKSPACE_URL: "https://admin.boldworkspace.com.br"
                                }
                              }
                            }
                          }, null, 2)}

                          {mcpConfigType === "cursor" && JSON.stringify({
                            mcpServers: {
                              "bold-workspace": {
                                url: "https://admin.boldworkspace.com.br/api/mcp/sse",
                                headers: {
                                  Authorization: `Bearer ${mcpToken}`
                                }
                              }
                            }
                          }, null, 2)}

                          {mcpConfigType === "windsurf" && JSON.stringify({
                            mcpServers: {
                              "bold-workspace": {
                                command: "npx",
                                args: ["-y", "@boldworkspace/mcp-server@latest"],
                                env: {
                                  BOLD_WORKSPACE_API_KEY: mcpToken
                                }
                              }
                            }
                          }, null, 2)}

                          {mcpConfigType === "sdk" && `import { BoldMCPClient } from "@boldworkspace/mcp-sdk"\n\nconst mcp = new BoldMCPClient({\n  apiKey: "${mcpToken}",\n  endpoint: "https://admin.boldworkspace.com.br/api/mcp"\n})\n\n// Chamar ferramenta do Bold Workspace\nconst apps = await mcp.callTool("bold_workspace_list_apps")\nconsole.log(apps)`}
                        </pre>
                        <Button
                          size="sm"
                          onClick={() => {
                            let textToCopy = ""
                            if (mcpConfigType === "claude") {
                              textToCopy = JSON.stringify({
                                mcpServers: {
                                  "bold-workspace": {
                                    command: "npx",
                                    args: ["-y", "@boldworkspace/mcp-server@latest"],
                                    env: {
                                      BOLD_WORKSPACE_API_KEY: mcpToken,
                                      BOLD_WORKSPACE_URL: "https://admin.boldworkspace.com.br"
                                    }
                                  }
                                }
                              }, null, 2)
                            } else if (mcpConfigType === "cursor") {
                              textToCopy = JSON.stringify({
                                mcpServers: {
                                  "bold-workspace": {
                                    url: "https://admin.boldworkspace.com.br/api/mcp/sse",
                                    headers: { Authorization: `Bearer ${mcpToken}` }
                                  }
                                }
                              }, null, 2)
                            } else if (mcpConfigType === "windsurf") {
                              textToCopy = JSON.stringify({
                                mcpServers: {
                                  "bold-workspace": {
                                    command: "npx",
                                    args: ["-y", "@boldworkspace/mcp-server@latest"],
                                    env: { BOLD_WORKSPACE_API_KEY: mcpToken }
                                  }
                                }
                              }, null, 2)
                            } else {
                              textToCopy = `import { BoldMCPClient } from "@boldworkspace/mcp-sdk"\nconst mcp = new BoldMCPClient({ apiKey: "${mcpToken}", endpoint: "https://admin.boldworkspace.com.br/api/mcp" })`
                            }
                            navigator.clipboard.writeText(textToCopy)
                            setCopiedMcpConfig(true)
                            setTimeout(() => setCopiedMcpConfig(false), 2000)
                          }}
                          className="absolute top-3 right-3 bg-white/10 hover:bg-white/20 text-white text-xs h-7 px-2.5 backdrop-blur cursor-pointer"
                        >
                          {copiedMcpConfig ? <Check className="size-3.5 mr-1" /> : <Copy className="size-3.5 mr-1" />}
                          {copiedMcpConfig ? "Copiado!" : "Copiar JSON"}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Registro de Ferramentas MCP (Tools Exposed) */}
                  <Card className="border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-bold text-zinc-900 dark:text-white flex items-center gap-2">
                        <Zap className="size-4 text-[#004EB1]" />
                        Ferramentas Expostas (MCP Tools Registry)
                      </CardTitle>
                      <CardDescription className="text-xs">
                        Estas são as ferramentas do Bold Workspace que assistentes de IA podem executar diretamente.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {[
                          {
                            name: "bold_workspace_list_apps",
                            desc: "Lista aplicativos instalados e atalhos ativos no workspace.",
                            tag: "read:apps",
                          },
                          {
                            name: "bold_workspace_create_notification",
                            desc: "Envia alertas em tempo real no Desk e Mobile do usuário.",
                            tag: "write:notifications",
                          },
                          {
                            name: "bold_workspace_get_profile",
                            desc: "Consulta dados da conta, licenças e estatísticas ativas.",
                            tag: "read:profile",
                          },
                          {
                            name: "bold_workspace_search",
                            desc: "Busca contatos, notas e configurações no ecossistema Bold.",
                            tag: "read:workspace",
                          },
                          {
                            name: "bold_workspace_trigger_webhook",
                            desc: "Dispara automações e eventos registrados via Webhook.",
                            tag: "write:webhooks",
                          },
                        ].map((t) => (
                          <div
                            key={t.name}
                            className="p-3 rounded-xl border border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-950/60 flex flex-col justify-between gap-2"
                          >
                            <div>
                              <div className="flex items-center justify-between">
                                <span className="font-mono text-xs font-bold text-[#004EB1] dark:text-blue-400">{t.name}</span>
                                <span className="text-[9px] font-extrabold bg-blue-500/10 text-blue-600 dark:text-blue-400 px-2 py-0.5 rounded-full">
                                  {t.tag}
                                </span>
                              </div>
                              <p className="text-[11px] text-zinc-600 dark:text-zinc-400 mt-1 leading-snug">{t.desc}</p>
                            </div>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={async () => {
                                setMcpTestingTool(t.name)
                                try {
                                  const res = await fetch("/api/mcp", {
                                    method: "POST",
                                    headers: { "Content-Type": "application/json" },
                                    body: JSON.stringify({
                                      jsonrpc: "2.0",
                                      id: 1,
                                      method: "tools/call",
                                      params: { name: t.name, arguments: { query: "teste" } }
                                    })
                                  })
                                  const data = await res.json()
                                  setMcpTestResult(JSON.stringify(data.result || data, null, 2))
                                  toast.success(`Ferramenta ${t.name} executada com sucesso!`)
                                } catch (e) {
                                  toast.error("Erro ao testar ferramenta MCP.")
                                } finally {
                                  setMcpTestingTool(null)
                                }
                              }}
                              className="text-[10px] h-7 font-bold text-zinc-700 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-800 justify-start px-2 cursor-pointer w-fit"
                            >
                              <Play className="size-3 mr-1 text-emerald-500" />
                              {mcpTestingTool === t.name ? "Executando..." : "Testar Execução"}
                            </Button>
                          </div>
                        ))}
                      </div>

                      {/* Modal/Box de Resultado do Teste */}
                      {mcpTestResult && (
                        <div className="rounded-xl bg-zinc-950 p-4 border border-zinc-800 space-y-2 mt-3 font-sans">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-bold text-emerald-400 flex items-center gap-1.5">
                              <CheckCircle2 className="size-4" /> Resposta da Ferramenta MCP (200 OK)
                            </span>
                            <button
                              onClick={() => setMcpTestResult(null)}
                              className="text-xs text-zinc-400 hover:text-white cursor-pointer"
                            >
                              Fechar
                            </button>
                          </div>
                          <pre className="font-mono text-[10px] text-zinc-300 overflow-x-auto max-h-48 scrollbar-none">
                            {mcpTestResult}
                          </pre>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* SUB-ABA 5: SISTEMA DE TOASTS SHADCN (DEMO & TESTER) */}
                <TabsContent value="toasts" className="outline-none space-y-6">
                  <Card className="border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 font-sans">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-bold text-zinc-900 dark:text-white flex items-center gap-2">
                        <Sparkles className="size-4 text-[#004EB1]" />
                        Notificações Toast (Padrão shadcn/ui base + sonner)
                      </CardTitle>
                      <CardDescription className="text-xs">
                        Clique nos botões abaixo para disparar e testar todas as variantes de notificações em tempo real.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      {/* Grid de Botões de Teste */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                        {/* 1. SUCCESS */}
                        <div className="p-4 rounded-xl border border-emerald-200 dark:border-emerald-900/40 bg-emerald-50/50 dark:bg-emerald-950/20 space-y-2">
                          <div className="flex items-center gap-2">
                            <CheckCircle2 className="size-4 text-emerald-600 dark:text-emerald-400" />
                            <span className="text-xs font-bold text-emerald-900 dark:text-emerald-300">Success (Sucesso)</span>
                          </div>
                          <p className="text-[11px] text-zinc-500 dark:text-zinc-400">
                            Dispara toast de confirmação positiva na cor verde.
                          </p>
                          <Button
                            type="button"
                            size="sm"
                            onClick={() =>
                              toast.success("Alterações salvas com sucesso!", {
                                description: "As configurações do workspace foram atualizadas e sincronizadas.",
                              })
                            }
                            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold h-8 cursor-pointer"
                          >
                            Testar Success
                          </Button>
                        </div>

                        {/* 2. INFO */}
                        <div className="p-4 rounded-xl border border-blue-200 dark:border-blue-900/40 bg-blue-50/50 dark:bg-blue-950/20 space-y-2">
                          <div className="flex items-center gap-2">
                            <Info className="size-4 text-blue-600 dark:text-blue-400" />
                            <span className="text-xs font-bold text-blue-900 dark:text-blue-300">Info (Informação)</span>
                          </div>
                          <p className="text-[11px] text-zinc-500 dark:text-zinc-400">
                            Mensagem informativa ou aviso do sistema na cor azul.
                          </p>
                          <Button
                            type="button"
                            size="sm"
                            onClick={() =>
                              toast.info("Nova atualização disponível", {
                                description: "A versão v2.4.0 do Bold Workspace inclui integração nativa com MCP.",
                              })
                            }
                            className="w-full bg-[#004EB1] hover:bg-[#003d8d] text-white text-xs font-semibold h-8 cursor-pointer"
                          >
                            Testar Info
                          </Button>
                        </div>

                        {/* 3. WARNING */}
                        <div className="p-4 rounded-xl border border-amber-200 dark:border-amber-900/40 bg-amber-50/50 dark:bg-amber-950/20 space-y-2">
                          <div className="flex items-center gap-2">
                            <Shield className="size-4 text-amber-600 dark:text-amber-400" />
                            <span className="text-xs font-bold text-amber-900 dark:text-amber-300">Warning (Aviso)</span>
                          </div>
                          <p className="text-[11px] text-zinc-500 dark:text-zinc-400">
                            Alerta ou aviso importante na cor amarela/laranja.
                          </p>
                          <Button
                            type="button"
                            size="sm"
                            onClick={() =>
                              toast.warning("Atenção ao limite da cota", {
                                description: "Você consumiu 85% do espaço de armazenamento do workspace atual.",
                              })
                            }
                            className="w-full bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold h-8 cursor-pointer"
                          >
                            Testar Warning
                          </Button>
                        </div>

                        {/* 4. ERROR */}
                        <div className="p-4 rounded-xl border border-red-200 dark:border-red-900/40 bg-red-50/50 dark:bg-red-950/20 space-y-2">
                          <div className="flex items-center gap-2">
                            <Lock className="size-4 text-red-600 dark:text-red-400" />
                            <span className="text-xs font-bold text-red-900 dark:text-red-300">Error (Erro)</span>
                          </div>
                          <p className="text-[11px] text-zinc-500 dark:text-zinc-400">
                            Mensagem crítica ou falha de sistema na cor vermelha.
                          </p>
                          <Button
                            type="button"
                            size="sm"
                            onClick={() =>
                              toast.error("Falha de conexão com a API", {
                                description: "Não foi possível autenticar a chave secreta. Tente novamente.",
                              })
                            }
                            className="w-full bg-red-600 hover:bg-red-700 text-white text-xs font-semibold h-8 cursor-pointer"
                          >
                            Testar Error
                          </Button>
                        </div>

                        {/* 5. LOADING */}
                        <div className="p-4 rounded-xl border border-zinc-200 dark:border-zinc-800 bg-zinc-50/50 dark:bg-zinc-950/30 space-y-2">
                          <div className="flex items-center gap-2">
                            <Clock className="size-4 text-zinc-600 dark:text-zinc-400" />
                            <span className="text-xs font-bold text-zinc-900 dark:text-zinc-200">Loading (Carregando)</span>
                          </div>
                          <p className="text-[11px] text-zinc-500 dark:text-zinc-400">
                            Spinner de progresso assíncrono programático.
                          </p>
                          <Button
                            type="button"
                            size="sm"
                            onClick={() => {
                              const tId = toast.loading("Processando solicitação...", {
                                description: "Sincronizando dados com o servidor...",
                              })
                              setTimeout(() => {
                                toast.dismiss(tId)
                                toast.success("Processamento concluído com sucesso!")
                              }, 2500)
                            }}
                            className="w-full bg-zinc-800 hover:bg-zinc-700 text-white text-xs font-semibold h-8 cursor-pointer"
                          >
                            Testar Loading
                          </Button>
                        </div>

                        {/* 6. PROMISE */}
                        <div className="p-4 rounded-xl border border-purple-200 dark:border-purple-900/40 bg-purple-50/50 dark:bg-purple-950/20 space-y-2">
                          <div className="flex items-center gap-2">
                            <Zap className="size-4 text-purple-600 dark:text-purple-400" />
                            <span className="text-xs font-bold text-purple-900 dark:text-purple-300">Promise (Assíncrono)</span>
                          </div>
                          <p className="text-[11px] text-zinc-500 dark:text-zinc-400">
                            Transição automática entre Loading e Success.
                          </p>
                          <Button
                            type="button"
                            size="sm"
                            onClick={() => {
                              const dummyPromise = new Promise((resolve) => setTimeout(resolve, 2000))
                              toast.promise(dummyPromise, {
                                loading: "Enviando arquivo para a nuvem...",
                                success: "Upload concluído e validado!",
                                error: "Falha ao enviar arquivo.",
                              })
                            }}
                            className="w-full bg-purple-600 hover:bg-purple-700 text-white text-xs font-semibold h-8 cursor-pointer"
                          >
                            Testar Promise
                          </Button>
                        </div>

                        {/* 7. CALL TO ACTION (CTA) DE APLICATIVO VIA BRIDGE */}
                        <div className="p-4 rounded-xl border border-indigo-200 dark:border-indigo-900/40 bg-indigo-50/50 dark:bg-indigo-950/20 space-y-2 sm:col-span-2 md:col-span-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <ExternalLink className="size-4 text-indigo-600 dark:text-indigo-400" />
                              <span className="text-xs font-bold text-indigo-900 dark:text-indigo-300">
                                Call to Action (CTA) do Aplicativo via Iframe Bridge
                              </span>
                            </div>
                            <span className="text-[10px] font-mono font-bold bg-indigo-100 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300 px-2 py-0.5 rounded-full">
                              Iframe Bridge API
                            </span>
                          </div>
                          <p className="text-[11px] text-zinc-500 dark:text-zinc-400">
                            Testa o envio de notificações com ícone do aplicativo, cor semântica e botão de Ação (CTA) interativo que abre URLs de destino.
                          </p>
                          <Button
                            type="button"
                            size="sm"
                            onClick={() => {
                              window.postMessage({
                                type: "BOLD_SEND_NOTIFICATION",
                                payload: {
                                  title: "Pedido #1042 Recebido",
                                  text: "Nova venda confirmada no valor de R$ 450,00",
                                  variant: "success",
                                  avatarUrl: "https://cdn.boldworkspace.com.br/icons/app-default.png",
                                  action: {
                                    label: "Ver Pedido na Loja",
                                    url: "https://alpha.boldworkspace.com.br"
                                  }
                                }
                              }, "*")
                            }}
                            className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold h-8 px-4 cursor-pointer gap-2"
                          >
                            <ExternalLink className="size-3.5" />
                            Simular Notificação de App com Botão CTA
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
            </TabsContent>
  )
}
